/**
 * Wattec Custom PC Builder — save, load and preview a build
 *
 * Three things live here, all of them sitting on top of Easify's own real
 * option list rather than replacing it:
 *
 *   Save    Turns whatever the customer has chosen into a short code, and a
 *           link containing that code. Nothing is stored on a server: the
 *           code *is* the build, so it works from any device, any browser,
 *           with no account and nothing to expire.
 *
 *   Load    Reads a code back and re-picks each component on Easify's own
 *           controls, one at a time, waiting after each so Easify can open
 *           up whatever the choice unlocks (choosing AM5 before its
 *           processors exist on the page, and so on).
 *
 *   Preview A full read of the finished build before the customer commits —
 *           every part, what each one added, the total, anything still
 *           missing — with Add to Cart right there.
 *
 * The one thing this file never does is touch the cart. Two things were
 * tested on this page and both failed, so neither is attempted here:
 * pressing the theme's Add to Cart button from a script leaves the cart
 * empty, and moving that button into a pop-up loses every chosen component
 * on the way to the cart (21 option lines became none). The preview is
 * therefore built into the page directly above the real button, which
 * never moves and is always pressed by the customer themselves.
 */
(function () {
  'use strict';

  /* A code is a version marker followed by one token per chosen component.
     The token is the first 8 characters of that component's own id in
     Easify, which is stable across price changes, reordering and renaming —
     so a code saved today still resolves tomorrow as long as the component
     itself is still sold. */
  var CODE_PREFIX = 'WPC1';
  var TOKEN_LENGTH = 8;
  var GROUP_SIZE = 4;

  /* The most units of one part a code records (see encode). */
  var MAX_REPEAT = 20;

  /* How long to let Easify settle after each component is re-picked during
     a load. Long enough for its conditional logic to open the next group,
     short enough that a full machine restores in a few seconds. */
  var STEP_DELAY = 420;
  var MAX_STALLED_PASSES = 6;

  var PLACEHOLDER_STARTS = ['default', 'none', 'no thanks', 'no custom', 'skip'];

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function button(className, text) {
    var node = el('button', className, text);
    node.type = 'button';
    return node;
  }

  function config() {
    var node = document.getElementById('wattec-pc-builder-config');
    if (!node) return {};
    try { return JSON.parse(node.textContent) || {}; } catch (error) { return {}; }
  }

  function money(amount, currency) {
    try {
      return amount.toLocaleString('en-GB', { style: 'currency', currency: currency || 'GBP' });
    } catch (error) {
      return '£' + Number(amount).toFixed(2);
    }
  }

  /* Easify pre-fills the optional groups with a placeholder ("Default",
     "No Thanks - Skip Monitor"), which is not the customer having chosen
     anything — those are left out of a saved build, and a build loaded
     without them simply keeps the placeholder it already has. */
  function isPlaceholder(item) {
    if (item.price) return false;
    var label = String(item.label || '').trim().toLowerCase();
    for (var i = 0; i < PLACEHOLDER_STARTS.length; i++) {
      if (label.indexOf(PLACEHOLDER_STARTS[i]) === 0) return true;
    }
    return false;
  }

  function tokenFor(valueId) {
    return String(valueId || '').replace(/[^0-9a-zA-Z]/g, '').slice(0, TOKEN_LENGTH).toLowerCase();
  }

  /** The code as the customer sees it: blocks of four, easy to read back. */
  function pretty(code) {
    var body = code.slice(CODE_PREFIX.length);
    var blocks = [];
    for (var i = 0; i < body.length; i += GROUP_SIZE) blocks.push(body.slice(i, i + GROUP_SIZE));
    return CODE_PREFIX + '-' + blocks.join('-');
  }

  function tidy(code) {
    return String(code || '').replace(/[^0-9a-zA-Z]/g, '');
  }

  /* ==================================================================
   * The builder's own save/load engine
   * ================================================================== */

  function Vault(adapter) {
    this.aris = adapter;
  }

  /** Everything the customer has actually chosen, in the order it was asked. */
  Vault.prototype.picks = function () {
    return this.aris.getSelection().filter(function (item) {
      return item.visible && !isPlaceholder(item);
    });
  };

  Vault.prototype.encode = function () {
    // A part chosen several times over (three of the same fan) is written
    // into the code once per unit, which keeps older codes valid as they
    // are. Capped so a mistyped quantity cannot make a code absurdly long.
    var tokens = [];
    this.picks().forEach(function (item) {
      var times = Math.min(Math.max(1, item.quantity || 1), MAX_REPEAT);
      for (var i = 0; i < times; i++) tokens.push(tokenFor(item.valueId));
    });
    if (!tokens.length) return null;
    return CODE_PREFIX + tokens.join('');
  };

  Vault.prototype.link = function (code) {
    if (!code) return null;
    var base = window.location.origin + window.location.pathname;
    var search = new URLSearchParams(window.location.search);
    search.delete('build');
    search.set('build', code);
    return base + '?' + search.toString();
  };

  /** Splits a pasted code back into tokens, or null if it isn't one. */
  Vault.prototype.parse = function (code) {
    var clean = tidy(code);
    if (clean.toUpperCase().indexOf(CODE_PREFIX) !== 0) return null;
    var body = clean.slice(CODE_PREFIX.length).toLowerCase();
    if (!body.length || body.length % TOKEN_LENGTH !== 0) return null;
    var tokens = [];
    for (var i = 0; i < body.length; i += TOKEN_LENGTH) tokens.push(body.slice(i, i + TOKEN_LENGTH));
    return tokens;
  };

  /** Every component currently on sale for this product, by its token. */
  Vault.prototype.catalogue = function () {
    var map = {};
    this.aris.discoverOptions().forEach(function (option) {
      option.values.forEach(function (value) {
        var token = tokenFor(value.id);
        if (!map[token]) map[token] = { option: option, value: value };
      });
    });
    return map;
  };

  Vault.prototype.isChosen = function (valueId) {
    return this.aris.getSelection().some(function (item) {
      return item.valueId === valueId && item.visible;
    });
  };

  /**
   * Re-picks each component from a code, one at a time.
   *
   * Order matters and cannot be known in advance: a processor's own list
   * only exists once its platform has been chosen. So rather than working
   * through the code in order, each pass picks the first component whose
   * group Easify is currently showing, then waits for Easify to catch up
   * and looks again. Anything still unreachable after several quiet passes
   * is reported back as not restored rather than silently dropped.
   */
  Vault.prototype.restore = function (tokens, onProgress) {
    var self = this;
    var known = this.catalogue();
    // A token repeated in the code is one part chosen that many times: it
    // is picked once, and its quantity box then set to the count.
    var counts = {};
    var order = [];
    tokens.forEach(function (token) {
      if (!counts[token]) { counts[token] = 0; order.push(token); }
      counts[token]++;
    });
    var queue = order.map(function (token) {
      return { token: token, count: counts[token], entry: known[token] || null, done: false, needsQty: counts[token] > 1 };
    });
    var unknown = queue.filter(function (item) { return !item.entry; }).length;
    var stalled = 0;

    function report() {
      var done = queue.filter(function (item) { return item.done; });
      if (onProgress) {
        onProgress({
          done: done.length,
          total: queue.length,
          current: done.length ? done[done.length - 1].entry.value.label : null
        });
      }
    }

    function summary() {
      var missed = queue.filter(function (item) { return !item.done; });
      return {
        restored: queue.length - missed.length,
        total: queue.length,
        unknown: unknown,
        missing: missed.map(function (item) {
          return item.entry ? {
            label: item.entry.value.label,
            section: item.entry.option.sectionTitle || item.entry.option.label
          } : { label: 'A part that is no longer available', section: '' };
        })
      };
    }

    return new Promise(function (resolve) {
      function pass() {
        var moved = false;

        // Quantities go on once their part is ticked, a pass after it was
        // picked, so Easify has already recorded the tick.
        queue.forEach(function (item) {
          if (!item.done || !item.needsQty) return;
          if (self.aris.setQuantity && self.aris.setQuantity(item.entry.value.id, item.count)) {
            item.needsQty = false;
            moved = true;
          }
        });

        var pending = queue.filter(function (item) { return !item.done && item.entry; });
        var awaitingQty = queue.some(function (item) { return item.done && item.needsQty; });
        if (!pending.length && !awaitingQty) return resolve(summary());
        for (var i = 0; i < pending.length; i++) {
          var item = pending[i];
          if (!self.aris.isVisible(item.entry.option)) continue;

          if (self.isChosen(item.entry.value.id)) {
            item.done = true;
            moved = true;
            break;
          }
          if (self.aris.setSelection(item.entry.value.id)) {
            item.done = true;
            moved = true;
            break;
          }
        }

        report();
        stalled = moved ? 0 : stalled + 1;
        if (stalled > MAX_STALLED_PASSES) return resolve(summary());
        window.setTimeout(pass, STEP_DELAY);
      }
      pass();
    });
  };

  /* ==================================================================
   * The panel the customer sees
   * ================================================================== */

  function Panel(adapter, vault) {
    this.aris = adapter;
    this.vault = vault;
    this.view = 'preview';
    this.build();
  }

  /**
   * The panel is built into the page rather than floating over it, and it
   * is placed immediately above the theme's own Add to Cart button, so the
   * customer reads their finished build and the button to buy it is the
   * very next thing on screen.
   *
   * It is deliberately NOT a pop-up that borrows the real button: moving
   * that button anywhere else in the page stops Easify's own click handler
   * recording the chosen components, and the build reaches the cart as a
   * bare machine with no options on it (tested — 21 option lines became
   * none). The button therefore never moves.
   */
  Panel.prototype.build = function () {
    var self = this;

    var panel = el('section', 'wps-panel');
    panel.hidden = true;

    var head = el('div', 'wps-head');
    this.tabs = el('div', 'wps-tabs');
    this.previewTab = button('wps-tab is-active', 'Your build');
    this.saveTab = button('wps-tab', 'Save or load');
    this.previewTab.addEventListener('click', function () { self.show('preview'); });
    this.saveTab.addEventListener('click', function () { self.show('save'); });
    this.tabs.appendChild(this.previewTab);
    this.tabs.appendChild(this.saveTab);
    head.appendChild(this.tabs);

    var closeBtn = button('wps-close', '×');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', function () { self.close(); });
    head.appendChild(closeBtn);
    panel.appendChild(head);

    this.body = el('div', 'wps-body');
    panel.appendChild(this.body);

    this.panel = panel;
  };

  /**
   * Puts the panel in the rail, directly above Add to Cart. If the rail
   * isn't there (the guide failed to load), it goes above the button
   * wherever that button happens to be, so the panel is never orphaned.
   */
  Panel.prototype.mount = function () {
    if (this.panel.parentNode) return;
    var submit = document.querySelector('.product-form__submit');
    if (submit && submit.parentNode) {
      submit.parentNode.insertBefore(this.panel, submit);
      return;
    }
    var rail = document.querySelector('.wpg-rail');
    if (rail) rail.appendChild(this.panel);
  };

  Panel.prototype.open = function (view) {
    this.mount();
    this.panel.hidden = false;
    this.show(view || 'preview');

    var panel = this.panel;
    window.setTimeout(function () {
      var header = document.querySelector('.header-wrapper, header, .section-header');
      var offset = header ? header.getBoundingClientRect().height : 0;
      var top = panel.getBoundingClientRect().top + window.pageYOffset - offset - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 60);
  };

  Panel.prototype.close = function () {
    this.panel.hidden = true;
  };

  Panel.prototype.show = function (view) {
    this.view = view;
    this.previewTab.classList.toggle('is-active', view === 'preview');
    this.saveTab.classList.toggle('is-active', view === 'save');
    this.render();
  };

  Panel.prototype.render = function () {
    // Nothing to redraw while it is closed — it is rebuilt fresh on open.
    if (this.panel.hidden) return;
    this.body.innerHTML = '';
    if (this.view === 'preview') this.renderPreview();
    else this.renderSave();
  };

  Panel.prototype.renderPreview = function () {
    var currency = config().currency || 'GBP';
    var body = this.body;

    /* "Choose Your Platform", "Choose Your Graphic Card Option" and the
       like are the questions that lead to a component, not components in
       their own right — the answer to each is already visible in the part
       chosen right below it. They stay in a saved build (a build cannot be
       put back without them) but they are left out of the list the
       customer reads. */
    var picks = this.vault.picks().filter(function (item) {
      return !/^choose\s+your\b/i.test(String(item.optionLabel || ''));
    });

    if (!picks.length) {
      body.appendChild(el('p', 'wps-empty',
        'Nothing is chosen yet. Pick your components and your full build will be listed here.'));
      return;
    }

    var chosenCase = picks.filter(function (item) { return item.section === 'case'; })[0];
    if (chosenCase && chosenCase.image) {
      var figure = el('div', 'wps-hero');
      var image = el('img', 'wps-hero__img');
      image.src = chosenCase.image;
      image.alt = chosenCase.label;
      image.loading = 'lazy';
      figure.appendChild(image);
      var caption = el('div', 'wps-hero__text');
      caption.appendChild(el('div', 'wps-hero__title', config().productTitle || 'Your custom PC'));
      caption.appendChild(el('div', 'wps-hero__case', chosenCase.label));
      figure.appendChild(caption);
      body.appendChild(figure);
    }

    var list = el('ul', 'wps-list');
    picks.forEach(function (item) {
      var row = el('li', 'wps-row');

      var media = el('div', 'wps-row__media');
      if (item.image) {
        var thumb = el('img');
        thumb.src = item.image;
        thumb.alt = '';
        thumb.loading = 'lazy';
        media.appendChild(thumb);
      }
      row.appendChild(media);

      var text = el('div', 'wps-row__text');
      text.appendChild(el('div', 'wps-row__section', item.sectionTitle || item.optionLabel));
      text.appendChild(el('div', 'wps-row__label',
        (item.quantity > 1 ? item.quantity + ' × ' : '') + item.label));
      if (item.spec) text.appendChild(el('div', 'wps-row__spec', item.spec));
      row.appendChild(text);

      row.appendChild(el('div', 'wps-row__price', item.price ? '+' + money(item.price, currency) : 'Included'));
      list.appendChild(row);
    });
    body.appendChild(list);

    var breakdown = this.aris.getPriceBreakdown();
    var totals = el('div', 'wps-totals');
    var baseRow = el('div', 'wps-totals__row');
    baseRow.appendChild(el('span', null, 'Base system'));
    baseRow.appendChild(el('span', null, money(breakdown.base, currency)));
    totals.appendChild(baseRow);

    var addRow = el('div', 'wps-totals__row');
    addRow.appendChild(el('span', null, 'Your components'));
    addRow.appendChild(el('span', null, '+' + money(breakdown.addons, currency)));
    totals.appendChild(addRow);

    var totalRow = el('div', 'wps-totals__row wps-totals__row--total');
    totalRow.appendChild(el('span', null, 'Total'));
    totalRow.appendChild(el('span', null, breakdown.displayed || money(breakdown.total, currency)));
    totals.appendChild(totalRow);
    body.appendChild(totals);

    var check = this.aris.validate();
    if (!check.valid) {
      var warn = el('div', 'wps-warn');
      warn.appendChild(el('div', 'wps-warn__head', 'Still to choose'));
      var missing = el('ul', 'wps-warn__list');
      check.missing.forEach(function (item) {
        missing.appendChild(el('li', null, item.section || item.label));
      });
      warn.appendChild(missing);
      body.appendChild(warn);
    }

    var self = this;

    /* The theme's own Add to Cart button is the next thing under this
       panel, so the customer can go straight from reading their build to
       buying it — it is never moved or copied into here, because only the
       real button records the chosen components against the order. */
    if (check.valid) {
      body.appendChild(el('div', 'wps-ready',
        'Happy with this build? Add to Cart is just below.'));
    }

    var saveLine = el('div', 'wps-saveline');
    saveLine.appendChild(el('span', null, 'Not ready to order?'));
    var saveBtn = button('wps-link', 'Save this build for later');
    saveBtn.addEventListener('click', function () { self.show('save'); });
    saveLine.appendChild(saveBtn);
    body.appendChild(saveLine);
  };

  Panel.prototype.renderSave = function () {
    var self = this;
    var body = this.body;

    /* ---- save ---------------------------------------------------- */
    var saveBox = el('div', 'wps-box');
    saveBox.appendChild(el('h3', 'wps-box__head', 'Save this build'));

    var code = this.vault.encode();
    if (!code) {
      saveBox.appendChild(el('p', 'wps-box__note',
        'Choose some components first, then come back here to save your build.'));
    } else {
      saveBox.appendChild(el('p', 'wps-box__note',
        'Keep this code or link. Open it on any device to bring this exact build back.'));

      var codeRow = el('div', 'wps-copy');
      var codeField = el('input', 'wps-field');
      codeField.type = 'text';
      codeField.readOnly = true;
      codeField.value = pretty(code);
      codeField.addEventListener('focus', function () { codeField.select(); });
      codeRow.appendChild(codeField);
      codeRow.appendChild(this.copyButton(function () { return pretty(code); }, 'Copy code'));
      saveBox.appendChild(codeRow);

      var linkRow = el('div', 'wps-copy');
      var linkField = el('input', 'wps-field');
      linkField.type = 'text';
      linkField.readOnly = true;
      linkField.value = this.vault.link(code);
      linkField.addEventListener('focus', function () { linkField.select(); });
      linkRow.appendChild(linkField);
      linkRow.appendChild(this.copyButton(function () { return self.vault.link(code); }, 'Copy link'));
      saveBox.appendChild(linkRow);
    }
    body.appendChild(saveBox);

    /* ---- load ---------------------------------------------------- */
    var loadBox = el('div', 'wps-box');
    loadBox.appendChild(el('h3', 'wps-box__head', 'Load a saved build'));
    loadBox.appendChild(el('p', 'wps-box__note',
      'Paste a build code below and we will put every part back for you.'));

    var loadRow = el('div', 'wps-copy');
    var input = el('input', 'wps-field');
    input.type = 'text';
    input.placeholder = CODE_PREFIX + '-XXXX-XXXX-…';
    input.value = this.pendingCode ? pretty(this.pendingCode) : '';
    loadRow.appendChild(input);
    var loadBtn = button('wps-go', 'Load build');
    loadRow.appendChild(loadBtn);
    loadBox.appendChild(loadRow);

    var status = el('div', 'wps-status');
    loadBox.appendChild(status);
    body.appendChild(loadBox);

    loadBtn.addEventListener('click', function () {
      self.runRestore(input.value, status, loadBtn);
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') { event.preventDefault(); loadBtn.click(); }
    });

    if (this.pendingCode) {
      this.pendingCode = null;
      window.setTimeout(function () { loadBtn.click(); }, 200);
    }
  };

  Panel.prototype.copyButton = function (getText, label) {
    var node = button('wps-copybtn', label);
    node.addEventListener('click', function () {
      var text = getText();
      function done() {
        node.textContent = 'Copied';
        window.setTimeout(function () { node.textContent = label; }, 1600);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, function () { fallback(); });
      } else {
        fallback();
      }
      function fallback() {
        var temp = document.createElement('textarea');
        temp.value = text;
        temp.setAttribute('readonly', 'readonly');
        temp.style.position = 'fixed';
        temp.style.opacity = '0';
        document.body.appendChild(temp);
        temp.select();
        try { document.execCommand('copy'); done(); } catch (error) { node.textContent = 'Copy failed'; }
        document.body.removeChild(temp);
      }
    });
    return node;
  };

  Panel.prototype.runRestore = function (raw, status, trigger) {
    var self = this;
    var tokens = this.vault.parse(raw);

    status.className = 'wps-status';
    if (!tokens) {
      status.classList.add('is-bad');
      status.textContent = 'That code is not one of ours. Build codes start with ' + CODE_PREFIX + '.';
      return;
    }

    trigger.disabled = true;
    status.classList.add('is-working');
    status.textContent = 'Putting your build back together…';

    this.vault.restore(tokens, function (progress) {
      status.textContent = 'Restoring your build — ' + progress.done + ' of ' + progress.total +
        ' parts' + (progress.current ? ' (' + progress.current + ')' : '') + '…';
    }).then(function (result) {
      trigger.disabled = false;
      status.className = 'wps-status';

      if (result.restored === result.total) {
        status.classList.add('is-good');
        status.textContent = 'Your build is back — all ' + result.total + ' parts restored.';
        window.setTimeout(function () { self.show('preview'); }, 900);
        return;
      }

      status.classList.add('is-warn');
      var lost = result.missing.map(function (item) {
        return item.section ? item.section + ' (' + item.label + ')' : item.label;
      }).join(', ');
      status.textContent = 'Restored ' + result.restored + ' of ' + result.total +
        ' parts. We could not put back: ' + lost + '. Please choose ' +
        (result.missing.length > 1 ? 'those' : 'that') + ' again.';
    });
  };

  /* ==================================================================
   * Where the customer finds all this
   * ================================================================== */

  function addControls(panel) {
    var row = el('div', 'wps-controls');

    var previewBtn = button('wps-controls__btn wps-controls__btn--primary', 'Preview build');
    previewBtn.addEventListener('click', function () { panel.open('preview'); });
    row.appendChild(previewBtn);

    var saveBtn = button('wps-controls__btn', 'Save or load a build');
    saveBtn.addEventListener('click', function () { panel.open('save'); });
    row.appendChild(saveBtn);

    // Beside the build, above Add to Cart, where the customer is deciding.
    var rail = document.querySelector('.wpg-rail');
    var summary = rail && rail.querySelector('.wpb-summary');
    if (rail && summary) rail.insertBefore(row, summary.nextSibling);
    else if (rail) rail.appendChild(row);

    // And at the top of the step flow, for someone arriving with a code.
    var shell = document.querySelector('.wpg');
    if (shell) {
      var topRow = el('div', 'wps-controls wps-controls--top');
      var topSave = button('wps-controls__btn', 'Save or load a build');
      topSave.addEventListener('click', function () { panel.open('save'); });
      var topPreview = button('wps-controls__btn wps-controls__btn--primary', 'Preview build');
      topPreview.addEventListener('click', function () { panel.open('preview'); });
      topRow.appendChild(topPreview);
      topRow.appendChild(topSave);
      shell.insertBefore(topRow, shell.firstChild);
    }
  }

  function boot() {
    if (!window.WattecPCBuilder || !window.WattecPCBuilder.aris) return;
    var adapter = window.WattecPCBuilder.aris;

    adapter.ready().then(function () {
      var vault = new Vault(adapter);
      var panel = new Panel(adapter, vault);

      window.WattecPCBuilder.save = { vault: vault, panel: panel };

      // The panel is built after the guide, so its rail already exists.
      window.setTimeout(function () { addControls(panel); }, 0);

      // Keep the preview in step with the build behind it. The save/load
      // view is deliberately left alone — redrawing it would wipe whatever
      // code the customer is part way through pasting in.
      adapter.observeChanges(function () {
        if (panel.view === 'preview') panel.render();
      });

      // Someone opening a saved link lands straight in the load panel with
      // their code already filled in, and it restores itself from there.
      var shared = new URLSearchParams(window.location.search).get('build');
      if (shared && vault.parse(shared)) {
        panel.pendingCode = tidy(shared);
        panel.open('save');
      }
    }).catch(function (error) {
      if (window.console) console.warn('[Wattec save]', error.message);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
